/**
 * 后端 VO 与前端领域类型的互转（与 jpp-backend Facade DTO 对齐）
 */
import type {
  Product,
  ProductVariant,
  ProductDetailBlock,
  CartItem,
  Order,
  OrderItem,
  User,
  Address,
} from '#types/index';
import type {
  ItemListVO,
  ItemDetailVO,
  ItemDetailVOElement,
  ItemCreateReq,
  ItemUpdateReq,
  ItemInfoUpdateReq,
  Sku,
  SkuProperty,
  ItemDetailElement,
  TwoColumnTable,
  CartVO,
  CartItemVO,
  OrderVO,
  OrderItemVO,
  UserVO,
  AddressVO,
  BaseAddressReq,
} from '#types/api';
import { generateId } from '@utils/index';

export function itemListVOToProduct(vo: ItemListVO): Product {
  const now = new Date().toISOString();
  const raw = vo as ItemListVO & {
    stock?: number | string;
    inventory?: number | string;
    totalStock?: number | string;
    sellStatus?: string;
    skuList?: Array<{
      uuid?: string;
      id?: string;
      stock?: number | string;
      discountPrice?: number | string;
      regularPrice?: number | string;
      propertyList?: Array<{ key?: string; value?: string }>;
    }>;
  };
  const toNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return undefined;
  };
  const variants: ProductVariant[] = (raw.skuList ?? []).map((s, i) => ({
    id: s.uuid ?? s.id ?? `sku-${vo.uuid}-${i}`,
    skuKV: (s.propertyList ?? []).map((p) => ({
      propertyName: p.key ?? '',
      propertyValue: p.value ?? '',
    })),
    compareAtPrice: toNumber(s.regularPrice),
    price: toNumber(s.discountPrice) ?? 0,
    inventory: Math.max(0, Math.floor(toNumber(s.stock) ?? 0)),
  }));
  const hasSkuList = Array.isArray(raw.skuList) && raw.skuList.length > 0;
  const inventoryFromSku = variants.reduce((sum, v) => sum + (v.inventory ?? 0), 0);
  const inventoryFromField = toNumber(raw.inventory) ?? toNumber(raw.stock) ?? toNumber(raw.totalStock) ?? 0;

  return {
    id: vo.uuid,
    markId: vo.markId ?? '',
    title: vo.title ?? '',
    detail: [],
    images: [],
    imageUrls: vo.coverImgUrl ? [vo.coverImgUrl] : [],
    category: vo.categoryId ?? '',
    tags: vo.tags ?? [],
    inventory: hasSkuList ? inventoryFromSku : inventoryFromField,
    variants,
    status: raw.sellStatus === 'SELLING' ? 'active' : 'inactive',
    createdAt: now,
    updatedAt: now,
  };
}

function detailElementToBlock(el: ItemDetailVOElement, index: number): ProductDetailBlock {
  const id = `detail-${index}-${generateId()}`;
  if (el.type === 'TEXT') {
    return { id, type: 'text', text: (el as { text?: string }).text ?? '' };
  }
  if (el.type === 'IMG') {
    const imageId = (el as { imgId?: string }).imgId ?? '';
    const imageUrl = (el as { imgUrl?: string }).imgUrl;
    return { id, type: 'image', imageId, imageUrl };
  }
  if (el.type === 'TABLE') {
    const table = (el as { table?: TwoColumnTable[] }).table ?? [];
    const rows = table.map((t) => [t.column1 ?? '', t.column2 ?? '']);
    return { id, type: 'table', rows };
  }
  return { id, type: 'text', text: '' };
}

export function itemDetailVOToProduct(vo: ItemDetailVO): Product {
  const now = new Date().toISOString();
  const detail: ProductDetailBlock[] = (vo.detail ?? []).map((el, i) =>
    detailElementToBlock(el, i)
  );
  const variants: ProductVariant[] = (vo.skuList ?? []).map((s, i) => ({
    id: (s as { uuid?: string }).uuid ?? s.id ?? `sku-${vo.uuid}-${i}`,
    skuKV: (s.propertyList ?? []).map((p) => ({
      propertyName: p.key ?? '',
      propertyValue: p.value ?? '',
    })),
    compareAtPrice: s.regularPrice,
    price: s.discountPrice,
    inventory: s.stock,
  }));
  const images = [
    vo.coverImgFileId ?? '',
    ...(vo.imgFileIdList ?? []),
  ].filter((x) => !!x);
  const imageUrls = [
    vo.coverImgUrl ?? '',
    ...(vo.imgUrl ?? []),
  ].filter((x) => !!x);
  const inventory = variants.reduce((sum, v) => sum + (v.inventory ?? 0), 0);
  return {
    id: vo.uuid,
    markId: vo.markId ?? '',
    title: vo.title ?? '',
    detail,
    images,
    imageUrls,
    category: vo.categoryId ?? '',
    tags: vo.tags ?? [],
    inventory,
    variants,
    status: vo.sellStatus === 'SELLING' ? 'active' : 'inactive',
    createdAt: now,
    updatedAt: now,
  };
}

function productDetailBlockToElement(block: ProductDetailBlock): ItemDetailElement {
  if (block.type === 'text') {
    return { type: 'TEXT', text: block.text };
  }
  if (block.type === 'image') {
    return { type: 'IMG', imgId: block.imageId };
  }
  if (block.type === 'table') {
    const table: TwoColumnTable[] = (block.rows ?? []).map((row) => ({
      column1: row[0] ?? '',
      column2: row[1] ?? '',
    }));
    return { type: 'TABLE', table };
  }
  return { type: 'TEXT', text: '' };
}

function variantToSku(v: ProductVariant): Sku {
  const regularPrice = v.compareAtPrice ?? v.price ?? 0;
  return {
    propertyList: (v.skuKV ?? []).map((k): SkuProperty => ({
      key: k.propertyName ?? (k as { key?: string }).key ?? '',
      value: k.propertyValue ?? (k as { value?: string }).value ?? '',
    })),
    regularPrice,
    discountPrice: (v.price != null && v.price > 0) ? v.price : regularPrice,
    stock: Math.max(0, Math.floor(v.inventory ?? 0)),
  };
}

export function productToItemCreateRequest(p: Product): ItemCreateReq {
  const markId = (p.markId ?? '').trim();
  return {
    categoryId: p.category ?? '',
    title: p.title ?? '',
    coverImgFileId: p.images?.[0] ?? '',
    imgFileIdList: p.images?.slice(1) ?? [],
    skuList: (p.variants ?? []).map(variantToSku),
    detailElementList: (p.detail ?? []).map(productDetailBlockToElement),
    sellStatus: p.status === 'active' ? 'SELLING' : 'NOT_SELLING',
    tags: p.tags ?? [],
    markId,
  };
}

export function productToItemUpdate(p: Product): ItemUpdateReq {
  return {
    uuid: p.id,
    ...productToItemCreateRequest(p),
  };
}

export function productToComplianceItemUpdate(p: Product): ItemInfoUpdateReq {
  return {
    uuid: p.id,
    infoType: 'COMPLIANCE',
    title: p.title ?? '',
    coverImgFileId: p.images?.[0] ?? '',
    imgFileIdList: p.images?.slice(1) ?? [],
    detailElementList: (p.detail ?? []).map(productDetailBlockToElement),
  };
}

export function cartVOToCartItems(vo: CartVO): CartItem[] {
  return (vo.items ?? []).map((item: CartItemVO) => {
    const info = item.itemInfo;
    const product: Product = info
      ? {
          id: info.uuid ?? item.itemId,
          title: info.title ?? '',
          detail: [],
          images: [],
          imageUrls: info.coverImgUrl ? [info.coverImgUrl] : [],
          category: '',
          tags: info.tags ?? [],
          inventory: 0,
          variants: [],
          status: 'active',
          createdAt: '',
          updatedAt: '',
        }
      : ({} as Product);
    return {
      id: item.uuid,
      productId: item.itemId,
      variantId: item.itemSkuId,
      quantity: item.count,
      lineAmount: item.amount,
      skuPropertyList: (item.skuPropertyList ?? []).map((kv) => ({
        propertyName: kv.key ?? '',
        propertyValue: kv.value ?? '',
      })),
      product,
      variant: undefined,
    };
  });
}

function cartOrderItemVOToOrderItem(item: OrderItemVO, orderId: string): OrderItem {
  const info = item.itemInfo;
  const count = item.count ?? 1;
  const lineAmount = Number(item.amount ?? 0);
  const unitPrice = count > 0 ? lineAmount / count : 0;
  const product: Product = info
    ? {
        id: info.uuid ?? item.itemId ?? '',
        title: info.title ?? '',
        detail: [],
        images: [],
        imageUrls: info.coverImgUrl ? [info.coverImgUrl] : [],
        category: '',
        tags: info.tags ?? [],
        inventory: 0,
        variants: [],
        status: 'active',
        createdAt: '',
        updatedAt: '',
      }
    : ({} as Product);
  const skuKV = (item.skuPropertyList ?? []).map((kv) => ({
    propertyName: kv.key ?? '',
    propertyValue: kv.value ?? '',
  }));
  return {
    id: item.uuid ?? `${orderId}-${item.itemId}`,
    productId: item.itemId ?? '',
    variantId: item.itemSkuId,
    quantity: count,
    price: unitPrice,
    product,
    variant: skuKV.length > 0 ? { id: '', skuKV, price: unitPrice, compareAtPrice: undefined, inventory: 0 } : undefined,
  };
}

function mapOrderStatus(vo: OrderVO): Order['status'] {
  switch (vo.orderStatus) {
    case 'SHIPPED':
      return 'shipped';
    case 'PAY_SUCCESS':
    case 'CASH_ON_DELIVERY':
      return 'processing';
    case 'CONFIRMED':
    case 'CREATE':
      return 'pending';
    case 'CANCELED':
    case 'PAY_FAILED':
      return 'cancelled';
    default:
      return vo.logistics?.status === 'SHIPPED' ? 'shipped' : vo.payInfo ? 'processing' : 'pending';
  }
}

export function isCashOnDeliveryOrder(vo: OrderVO): boolean {
  return vo.payInfo?.payStatus === 'CASH_ON_DELIVERY'
    || vo.payInfo?.payChannel === 'CASH_ON_DELIVERY'
    || vo.orderStatus === 'CASH_ON_DELIVERY';
}

function mapPaymentStatus(vo: OrderVO): Order['paymentStatus'] {
  if (isCashOnDeliveryOrder(vo)) return 'cash_on_delivery';
  switch (vo.orderStatus) {
    case 'PAY_SUCCESS':
    case 'SHIPPED':
      return 'paid';
    case 'PAY_FAILED':
      return 'failed';
    case 'CANCELED':
    case 'CREATE':
    case 'CONFIRMED':
      return 'pending';
    default:
      return vo.payInfo ? 'paid' : 'pending';
  }
}

export function orderVOToOrder(vo: OrderVO): Order {
  const rawList = vo.orderItemList ?? [];
  const items: OrderItem[] = rawList.map((oi) => cartOrderItemVOToOrderItem(oi, vo.orderId));
  const addr = vo.address;
  const emptyAddr = { firstName: '', lastName: '', address1: '', city: '', province: '', country: '', zip: '', phone: '' };
  const shippingAddr = addr
    ? {
        ...emptyAddr,
        firstName: addr.firstName ?? '',
        lastName: addr.lastName ?? '',
        address1: addr.address1 ?? '',
        address2: addr.address2,
        city: addr.city ?? '',
        province: addr.provinceId ?? '',
        country: addr.countryId ?? '',
        zip: addr.postcode ?? '',
        phone: addr.phone,
      }
    : emptyAddr;
  const deliveryAmt = Number(vo.payInfo?.deliveryAmount ?? 0);
  const snapshotItemTotal = rawList.reduce((sum, item) => sum + Number(item.amount ?? 0), 0);
  const discountedItemTotal = Number(vo.couponInfo?.itemDiscountedAmount ?? snapshotItemTotal);
  const itemTotal = Number(vo.payInfo?.itemTotalAmount ?? (
    vo.payInfo?.totalAmount != null ? Number(vo.payInfo.totalAmount) - deliveryAmt : discountedItemTotal
  ));
  const totalAmt = Number(vo.payInfo?.totalAmount ?? itemTotal + deliveryAmt);
  return {
    id: vo.orderId,
    orderNumber: vo.orderNum ?? vo.orderId,
    userId: vo.userId ?? '',
    customerEmail: vo.customerEmail,
    items,
    status: mapOrderStatus(vo),
    total: totalAmt,
    subtotal: Number(vo.couponInfo?.itemOriginalAmount ?? itemTotal),
    tax: 0,
    shipping: deliveryAmt,
    shippingAddress: shippingAddr,
    billingAddress: shippingAddr,
    paymentMethod: vo.payInfo?.payChannel ?? '',
    paymentStatus: mapPaymentStatus(vo),
    payInfo: vo.payInfo,
    couponInfo: vo.couponInfo,
    shippingInfo: vo.logistics
      ? {
          carrier: vo.logistics.carrierName,
          trackingNumber: vo.logistics.logisticsNumber,
          status: vo.logistics.status === 'SHIPPED' ? 'in_transit' : 'pending',
        }
      : undefined,
    createdAt: vo.createdTime ?? '',
    updatedAt: vo.createdTime ?? '',
  };
}

export function userVOToUser(vo: UserVO): User {
  return {
    id: vo.uuid,
    email: vo.email ?? '',
    name: vo.email ?? 'User',
    role: 'customer',
    createdAt: '',
    updatedAt: '',
  };
}

export function addressVOToAddress(vo: AddressVO): Address {
  return {
    id: vo.uuid,
    firstName: vo.firstName,
    lastName: vo.lastName,
    address1: vo.address1,
    address2: vo.address2,
    city: vo.city,
    province: vo.provinceId ?? '',
    country: vo.countryId ?? '',
    zip: vo.postcode,
    phone: vo.phone,
  };
}

export function addressToBaseAddress(a: Address): BaseAddressReq {
  return {
    firstName: a.firstName,
    lastName: a.lastName,
    countryId: a.country,
    provinceId: a.province,
    city: a.city,
    address1: a.address1,
    address2: a.address2,
    postcode: a.zip,
    phone: a.phone,
  };
}

export function addressToAddressVO(a: Address): AddressVO {
  return {
    uuid: a.id ?? '',
    ...addressToBaseAddress(a),
  };
}
